"use client";

import { Product } from "@/types/product.types";
import { ProductCard } from "@/components/shared/ProductCard";
import { ProductListCard } from "@/components/shared/ProductListCard";
import { cn } from "@/lib/utils";
import { LayoutGrid, List, Grid3x2, Grid3x3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import NextImage from "next/image";
import { ProductCardSkeleton } from "@/components/shared/ProductCardSkeleton";

interface Props {
    count: number;
    products: Product[];
    isLoading?: boolean;
    view: "grid" | "list";
    gridSize: 12 | 16;
    onViewChange: (v: "grid" | "list") => void;
    onGridSizeChange: (v: 12 | 16) => void;
    onQuickView?: ((product: Product) => void) | undefined;
    showViewToggle?: boolean;
    showGridSize?: boolean;
}

export const CatalogGrid = ({
    count,
    products,
    isLoading = false,
    view,
    gridSize,
    onViewChange,
    onGridSizeChange,
    onQuickView,
    showViewToggle = true,
    showGridSize = false,
}: Props) => {

    /* LOADING STATE */
    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div className="h-4 w-40 bg-gray-200 animate-pulse rounded" />
                    <div className="flex items-center gap-4">
                        <div className="h-10 w-24 bg-gray-200 animate-pulse rounded" />
                    </div>
                </div>

                <div
                    className={cn(
                        "grid gap-4 sm:gap-6 lg:gap-8",
                        gridSize === 16
                            ? "grid-cols-2 lg:grid-cols-4"
                            : "grid-cols-1 lg:grid-cols-3"
                    )}
                >
                    {Array.from({ length: gridSize }).map((_, i) => (
                        <ProductCardSkeleton key={i} />
                    ))}
                </div>
            </div>
        );
    }

    /* EMPTY STATE */

    if (!products || products.length === 0) {
        return (
            <div className="w-full flex flex-col items-center justify-center text-center px-6 py-12 sm:px-8 sm:py-14 lg:py-20">

                <div className="mb-4 sm:mb-6">
                    <NextImage
                        src="/assets/landing/search/not-found.png"
                        alt="No product"
                        width={800}
                        height={80}
                        className="w-40 h-40 sm:w-40 sm:h-40 lg:w-40 lg:h-40"
                    />
                </div>

                <h3 className="font-brastika text-3xl sm:text-3xl lg:text-3xl text-primary-700">
                    Aucun produit trouvé
                </h3>


            </div>
        );
    }

    return (
        <div className="space-y-6">

            {/* HEADER */}

            <div className="flex items-center justify-between">

                <p className="text-sm text-gray-500">
                    Affichage de {products.length} de {count} produits
                </p>

                <div className="flex items-center gap-4">

                    {showGridSize && (
                        <div className="flex gap-1">

                            <Button
                                size="icon"
                                variant={gridSize === 12 ? "default" : "outline"}
                                onClick={() => onGridSizeChange(12)}
                            >
                                <Grid3x2 />
                            </Button>

                            <Button
                                size="icon"
                                variant={gridSize === 16 ? "default" : "outline"}
                                onClick={() => onGridSizeChange(16)}
                            >
                                <Grid3x3 />
                            </Button>

                        </div>
                    )}

                    {showViewToggle && (
                        <div className="hidden lg:flex gap-2">

                            <Button
                                size="icon"
                                variant={view === "grid" ? "default" : "outline"}
                                onClick={() => onViewChange("grid")}
                            >
                                <LayoutGrid />
                            </Button>

                            <Button
                                size="icon"
                                variant={view === "list" ? "default" : "outline"}
                                onClick={() => onViewChange("list")}
                            >
                                <List />
                            </Button>

                        </div>
                    )}

                </div>

            </div>

            {/* LIST VIEW */}

            <div className={cn(view === "list" ? "hidden lg:block" : "hidden")}>

                <div className="flex flex-col gap-6">

                    {products.map((product) => (
                        <ProductListCard
                            key={product.id}
                            product={product}
                            onQuickView={onQuickView}
                        />
                    ))}

                </div>

            </div>

            {/* GRID VIEW */}

            <div
                className={cn(
                    "grid gap-3 sm:gap-6 lg:gap-8",
                    view === "list" ? "lg:hidden" : "",
                    gridSize === 16
                        ? "grid-cols-2 lg:grid-cols-4"
                        : "grid-cols-2 lg:grid-cols-3"
                )}
            >

                {products.map((product) => (
                    <ProductCard
                        key={product.id}
                        product={product}
                        onQuickView={() => onQuickView?.(product)}
                    />
                ))}

            </div>

        </div>
    );
};